import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, Spin, Alert, Table, Button, Space } from "antd";
import { FileExcelOutlined, FilePdfOutlined } from "@ant-design/icons";
import { Document, Page, Text, View, pdf } from "@react-pdf/renderer";
import { saveAs } from "file-saver";
import APICall from "../../../APICall";
import EndPoints from "../../../APICall/EndPoints";
import { formatToINRForSummay } from "../../../components/FormateToINR/formatToINR";
import { TypeSpace, UplineTag } from "../../Users/Accounts/style";
import { labelObj } from "../../../Helper/constant";
import { ArrowRightOutlined } from "@ant-design/icons";
import { styles } from "../../../components/ThemeCardTable/style";

const TrialBalance = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;
    const fetchData = async () => {
      try {
        const res = await APICall("get", EndPoints.getTrialBalance);
        if (isMounted) {
          setData(res?.data?.data ?? res?.data);
        }
      } catch (err) {
        if (isMounted) setError(err?.message || "Failed to load trial balance");
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchData();
    return () => {
      isMounted = false;
    };
  }, []);

  const rows = useMemo(() => {
    if (!Array.isArray(data)) return [];
    return data.map((item, idx) => {
      const credit = item?.ledgerBalance >= 0 ? Number(item?.ledgerBalance || 0) : 0;
      const debit = item?.ledgerBalance < 0 ? Math.abs(Number(item?.ledgerBalance || 0)) : 0;
      return {
        key: `${item?.userId || idx}`,
        srno: idx + 1,
        name: item?.accountName,
        userId: item?.userId,
        debit,
        credit,
        accountType: item?.accountType,
      };
    });
  }, [data]);

  const totals = useMemo(() => {
    return rows.reduce(
      (acc, r) => {
        acc.debit += r.debit || 0;
        acc.credit += r.credit || 0;
        return acc;
      },
      { debit: 0, credit: 0 }
    );
  }, [rows]);

  const columns = [
    {
      title: "SR.NO.",
      dataIndex: "srno",
      key: "srno",
      align: "left",
      width: 90,
    },
    {
      title: "NAME",
      dataIndex: "name",
      key: "name",
      align: "left",
      render: (text, item) => {
        return (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              {`${item.name} (${item.userId})`}
              <TypeSpace size={[0, "small"]} wrap>
                <UplineTag color="magenta" bordered={false}>
                  {labelObj[item.accountType]}
                </UplineTag>
              </TypeSpace>
            </div>
            <div
              role="button"
              tabIndex={0}
              style={{ cursor: "pointer", color: "#1677ff", display: "flex", alignItems: "center", gap: 6 }}
              onClick={() => {
                if (item?.userId) {
                  navigate(`/user-ledger/${item.userId}`);
                }
              }}
              onKeyDown={(e) => {
                if ((e.key === "Enter" || e.key === " ") && item?.userId) {
                  navigate(`/user-ledger/${item.userId}`);
                }
              }}
            >
              <span>Ledger</span>
              <ArrowRightOutlined />
            </div>
          </div>
        );
      },
    },
    {
      title: "DEBIT",
      dataIndex: "debit",
      key: "debit",
      align: "right",
      width: 160,
      render: (val) =>
        val ? (
          <span style={{ color: "#cf1322", fontWeight: 600 }}>
            {formatToINRForSummay(val)}
          </span>
        ) : (
          "-"
        ),
    },
    {
      title: "CREDIT",
      dataIndex: "credit",
      key: "credit",
      align: "right",
      width: 160,
      render: (val) =>
        val ? (
          <span style={{ color: "#389e0d", fontWeight: 600 }}>
            {formatToINRForSummay(val)}
          </span>
        ) : (
          "-"
        ),
    },
  ];
  const trialBlance = totals.credit - totals.debit;

  // CSV Export function
  const handleExportCSV = () => {
    if (!rows || rows.length === 0) {
      return;
    }

    const csvColumns = [
      { title: "SR.NO.", dataIndex: "srno" },
      { title: "NAME", dataIndex: "name" },
      { title: "USER ID", dataIndex: "userId" },
      { title: "DEBIT", dataIndex: "debit" },
      { title: "CREDIT", dataIndex: "credit" },
    ];

    const header = csvColumns.map((col) => col.title).join(",");
    const csvRows = rows.map((row) => {
      return [
        row.srno,
        `"${row.name} (${row.userId})"`,
        row.userId,
        row.debit || "",
        row.credit || "",
      ].join(",");
    });

    // Add totals row
    const totalsRow = [
      "",
      '"Total"',
      "",
      totals.debit || "",
      totals.credit || "",
    ].join(",");

    const csv = [header, ...csvRows, totalsRow].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, "Trial_Balance_Report.csv");
  };

  // PDF Export function
  const handleExportPDF = async () => {
    if (!rows || rows.length === 0) {
      return;
    }

    const pdfColumns = [
      { title: "SR.NO.", dataIndex: "srno", width: 60 },
      { title: "NAME", dataIndex: "name", width: 200 },
      { title: "DEBIT", dataIndex: "debit", width: 120 },
      { title: "CREDIT", dataIndex: "credit", width: 120 },
    ];

    try {
      const pdfData = await pdf(
        <Document>
          <Page size="A2" style={styles.page}>
            <View style={styles.text}>
              <Text>Trial Balance Report</Text>
            </View>
            <View>
              <View style={styles.scriptHeader}>
                {pdfColumns.map((col, index) => (
                  <Text style={[styles.tableCell, { width: col.width }]} key={index}>
                    {col.title}
                  </Text>
                ))}
              </View>
              {rows.map((item, index) => (
                <View
                  style={index % 2 === 0 ? styles.tableRowEven : styles.tableRowOdd}
                  key={index}
                >
                  <Text style={[styles.tableCell, { width: 60 }]}>
                    {item.srno}
                  </Text>
                  <Text style={[styles.tableCell, { width: 200 }]}>
                    {`${item.name} (${item.userId})`}
                  </Text>
                  <Text
                    style={[
                      styles.tableCell,
                      { width: 120, color: item.debit ? "#cf1322" : undefined },
                    ]}
                  >
                    {item.debit ? formatToINRForSummay(item.debit) : "-"}
                  </Text>
                  <Text
                    style={[
                      styles.tableCell,
                      { width: 120, color: item.credit ? "#389e0d" : undefined },
                    ]}
                  >
                    {item.credit ? formatToINRForSummay(item.credit) : "-"}
                  </Text>
                </View>
              ))}
              {/* Totals Row */}
              <View style={[styles.tableRow, { backgroundColor: "#f0f0f0" }]}>
                <Text style={[styles.tableCell, { width: 60, fontWeight: "bold" }]}>
                  Total
                </Text>
                <Text style={[styles.tableCell, { width: 200 }]}></Text>
                <Text
                  style={[
                    styles.tableCell,
                    { width: 120, fontWeight: "bold", color: "#cf1322" },
                  ]}
                >
                  {formatToINRForSummay(totals.debit)}
                </Text>
                <Text
                  style={[
                    styles.tableCell,
                    { width: 120, fontWeight: "bold", color: "#389e0d" },
                  ]}
                >
                  {formatToINRForSummay(totals.credit)}
                </Text>
              </View>
            </View>
          </Page>
        </Document>
      ).toBlob();

      const pdfUrl = URL.createObjectURL(pdfData);
      const downloadLink = document.createElement("a");
      downloadLink.href = pdfUrl;
      downloadLink.download = "Trial_Balance_Report.pdf";
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      URL.revokeObjectURL(pdfUrl);
    } catch (error) {
      console.error("Error generating PDF:", error);
    }
  };

  return (
    <Card
      title="Trial Balance"
      extra={
        !loading && !error && rows.length > 0 && (
          <Space>
            <Button
              icon={<FileExcelOutlined />}
              onClick={handleExportCSV}
              type="default"
            >
              Export CSV
            </Button>
            <Button
              icon={<FilePdfOutlined />}
              onClick={handleExportPDF}
              type="default"
            >
              Export PDF
            </Button>
          </Space>
        )
      }
    >
      {loading && <Spin />}
      {!loading && error && <Alert type="error" message={error} />}
      {!loading && !error && (
        <div style={{ width: "100%", overflowX: "auto" }}>
          <Table
            columns={columns}
            dataSource={rows}
            pagination={false}
            bordered
            size="small"
            scroll={{ x: "max-content" }}
            summary={() => (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={2} align="center">
                  Trial Balance:  <span style={{ color: trialBlance<0?"#cf1322":"#389e0d", fontWeight: 600 }}>
                    {formatToINRForSummay(trialBlance || 0)}
                    </span>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1} align="right">
                  <span style={{ color: "#cf1322", fontWeight: 600 }}>
                    -{formatToINRForSummay(totals.debit)}
                  </span>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2} align="right">
                  <span style={{ color: "#389e0d", fontWeight: 600 }}>
                    {formatToINRForSummay(totals.credit)}
                  </span>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            )}
          />
        </div>)
      }
   </Card>
  );
};

export default TrialBalance;


